import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { NavigationExtras, Router } from '@angular/router';
import { Operation, OperationObserverService, OperationSuccessState } from '@x/common/operation';
import {
  IOrderRecreateConfig,
  TNavigateTo,
} from '@x/ecommerce-shop/app/core/order/services/order-recreate-config';
import { OrderRecreateService } from '@x/ecommerce-shop/app/core/order/services/order-recreate.service';
import { RoutingContextService } from '@x/ecommerce-shop/app/core/routing/routing-context.service';
import { ShopStorage } from '@x/ecommerce-shop/app/core/shop-storage/shop-storage.service';
import { ButtonSizes } from '@x/ecommerce-shop/app/core/utils/directives/button-size.directive';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'x-order-recreate-button',
  templateUrl: './order-recreate-button.component.html',
  styleUrls: ['./order-recreate-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderRecreateButtonComponent {
  @Input({ required: true }) orderId: number;
  @Input() navigateTo?: TNavigateTo;
  @Input() buttonSizes: ButtonSizes = 'medium';
  @Input() color: ThemePalette = 'primary';
  @Input() showRecreateResponseDialog: boolean = false;

  control$ = this.operationService.control((config: IOrderRecreateConfig) => {
    return this.orderRecreateService.recreateOperation$(config);
  });

  observerQ$ = this.orderRecreateService.qStatus$.pipe(startWith(Operation.create()));

  private get config() {
    if (!this.orderId) return;

    const config: IOrderRecreateConfig = {
      recreateOrderId: this.orderId,
      currentOrderId: this.shopStorage.orderToken?.orderId,
      showRecreateResponseDialog: this.showRecreateResponseDialog,
    };

    return config;
  }
  constructor(
    private shopStorage: ShopStorage,
    private orderRecreateService: OrderRecreateService,
    private operationService: OperationObserverService,
    private router: Router,
    private rc: RoutingContextService,
  ) {}

  async recreate(operation: Operation) {
    if (operation.status === 'loading') return;

    if (!this.config) return;

    await this.control$.activate(this.config).then((operation) => {
      if (this.orderRecreateService.processing) return;

      const orderId = operation.data?.data?.order?.id;
      const navigateTo = operation.data?.data?.navigateTo ?? this.navigateTo;

      if (!navigateTo) return;

      if (operation.state instanceof OperationSuccessState && orderId) {
        this.navigate(orderId, navigateTo);
      }
    });
  }

  private navigate(orderId: number, navigateTo: TNavigateTo) {
    if (navigateTo === 'checkout') {
      this.navigateToCheckout(orderId);
      return;
    }

    this.rc.goHome();
  }

  private navigateToCheckout(orderId: number) {
    const navOptions: NavigationExtras = this.router.url.includes('/checkout')
      ? { queryParamsHandling: 'preserve' }
      : { queryParams: { redirect: this.router.url.split('?')[0] } };

    this.router.navigate(['/checkout', `${orderId}`], navOptions);
  }
}
